/**
 * AW-2C-005 -- the Script merge-field contract, as testable values.
 *
 * WHY THIS IS A SEPARATE MODULE
 * -----------------------------
 * Exactly the reason `embeddedRun.js` gives for sitting beside the
 * 3,400-line page rather than inside it: ScriptPlay cannot be imported
 * under Jest in this repository (react-router-dom 7 declares a `main`
 * it does not ship, and the page pulls dozens of further imports behind
 * path aliases). A rule that lived only inside it could not be proven
 * at all -- and the rule here is one that must be: whether the token an
 * agent reads aloud resolves to the customer, to the agent, or to
 * nothing.
 *
 * So the parser and the customer resolution are values, and ScriptPlay
 * maps their output to JSX. The existing `data`/`quote`/`calc`/`agent`/
 * `option` resolution stays in the page, untouched; this module only
 * parses, and only resolves the source AW-2C-005 adds.
 */

/** The ids `$(customer:…)` accepts. */
export const CUSTOMER_TOKEN_IDS = Object.freeze([
  "first_name",
  "last_name",
  "city",
  "state",
]);

/**
 * The merge-field pattern.
 *
 * `customer` is ONE alternative added to the existing group -- not a
 * second syntax. An author writes `$(customer:first_name)` exactly as
 * they already write `$(agent:first_name)`, and every pre-existing
 * source parses to the same shape it always did.
 *
 * Rebuilt per call rather than shared: a `g`-flagged literal carries
 * mutable `lastIndex`, and a module-level one shared between two
 * callers silently skips matches in the second.
 */
function tokenPattern() {
  return /\$\((data|quote|calc|agent|customer):([a-zA-Z0-9_\-]+)\)|\$\(option:([123]):([a-zA-Z0-9_\-]+)\)/g;
}

/**
 * Split script text into literal runs and tokens.
 *
 * Returns `[]` for empty input. Unrecognised text is left as text --
 * a malformed `$(nope:x)` renders as itself rather than vanishing,
 * which is what lets an author see their own typo instead of a gap.
 */
export function parseScriptTokens(text) {
  const src = String(text ?? "");
  if (!src) return [];

  const parts = [];
  const re = tokenPattern();
  let last = 0;
  let match;

  while ((match = re.exec(src)) !== null) {
    const before = src.slice(last, match.index);
    if (before) parts.push({ type: "text", value: before });

    // $(data|quote|calc|agent|customer:ID)
    if (match[1] && match[2]) {
      parts.push({ type: "token", source: match[1], id: match[2] });
    }
    // $(option:N:FIELD_ID) -- Quote Sheet inputs per option
    if (match[3] && match[4]) {
      parts.push({ type: "option_token", optionIndex: Number(match[3]), id: match[4] });
    }

    last = match.index + match[0].length;
  }

  const tail = src.slice(last);
  if (tail) parts.push({ type: "text", value: tail });
  return parts;
}

/** What a `$(customer:…)` token resolved to. */
export const CUSTOMER_TOKEN_STATE = Object.freeze({
  VALUE: "value",
  PENDING: "pending",
  UNAVAILABLE: "unavailable",
  UNKNOWN_FIELD: "unknown_field",
});

/**
 * Resolve one `$(customer:ID)` token against the shared snapshot.
 *
 * **Never reads the agent.** `$(agent:first_name)` is the person making
 * the call; `$(customer:first_name)` is the person receiving it. A
 * fallback between them would put the agent's own name into the
 * greeting addressed to the customer, which is the worst thing this
 * feature could do. This function cannot even see the agent.
 *
 * **Never invents.** There is no default name, no "Customer", no
 * initial, and deliberately not the em dash the page's other sources
 * use for an unresolved value: "Hi —," read aloud is worse than a
 * visible gap, and the dash is indistinguishable from a real one in
 * the script's own text.
 *
 * `PENDING` while the snapshot is in flight, so the caller can render
 * something wordless rather than something wrong. `UNAVAILABLE` covers
 * both "the read was refused" and "this field is empty for this
 * customer" -- an agent cannot act on the difference mid-call, and the
 * route already recorded which it was server-side.
 */
export function resolveCustomerToken(id, customer) {
  if (!CUSTOMER_TOKEN_IDS.includes(id)) {
    return { state: CUSTOMER_TOKEN_STATE.UNKNOWN_FIELD, value: null };
  }
  if (customer?.loading) {
    return { state: CUSTOMER_TOKEN_STATE.PENDING, value: null };
  }
  const value = customer?.tokens?.[id];
  if (typeof value === "string" && value.trim()) {
    return { state: CUSTOMER_TOKEN_STATE.VALUE, value: value.trim() };
  }
  return { state: CUSTOMER_TOKEN_STATE.UNAVAILABLE, value: null };
}
